import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { of } from 'rxjs/observable/of';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Area } from '../../../core/constants/area';
import { Reminder } from './models/models';
import { ReminderRequest, toReminderRequest } from './models/requests';
import { ReminderResponse, toReminder } from './models/responses';

export interface DocumentInfo {
  id: number;
  documentName: string;
  documentTypeId: number;
  bLOBStoragePath: string;
  createdDate: Date;
  createdByUserId: number;
  lastModifiedDate: Date;
  lastModifiedByUserId: number;
  active: boolean;
}

function isDocumentInfo(obj: any): obj is DocumentInfo {
  return obj &&
    typeof obj.id === 'number' &&
    typeof obj.documentName === 'string' &&
    typeof obj.documentTypeId === 'number' &&
    typeof obj.bLOBStoragePath === 'string';
}

@Injectable()
export class RemindersService {
  private reminderUpdatedSubject = new Subject<void>();

  public reminderUpdated$ = this.reminderUpdatedSubject.asObservable();

  constructor(private httpClient: HttpClient) { }

  public notifyReminderUpdated(): void {
    this.reminderUpdatedSubject.next();
  }

  public getCompanyReminderCount(transportCompanyId: number): Observable<{ count: number, hasPastDue: boolean }> {
    const url = `${environment.dotNetApiUrl}/${Area.core}/reminders/CompanyCount/${transportCompanyId}`;

    return this.httpClient.get<any>(url).pipe(
      map(response => {
        if (response && typeof response === 'object') {
          return {
            count: response.count || 0,
            hasPastDue: response.hasPastDue || false
          };
        }
        return { count: 0, hasPastDue: false };
      }),
      catchError(error => {
        console.error(`Error fetching company reminder count: ${error}`);
        return of({ count: 0, hasPastDue: false });
      })
    );
  }

  public getDocumentInfo(documentId: number): Observable<DocumentInfo> {
    const url = `${environment.dotNetApiUrl}/${Area.core}/reminders/Documents`;

    return new Observable<DocumentInfo>(observer => {
      this.httpClient.get<any>(`${url}?documentIds=${documentId}`).subscribe(
        response => {
          try {
            if (response && Array.isArray(response) && response.length > 0) {
              const doc = response[0];

              const documentInfo: DocumentInfo = {
                id: doc.id || 0,
                documentName: doc.documentName || '',
                documentTypeId: doc.documentTypeId || 0,
                bLOBStoragePath: doc.blobStoragePath || doc.bLOBStoragePath || '',
                createdDate: doc.createdDate ? new Date(doc.createdDate) : new Date(),
                createdByUserId: doc.createdByUserId || 0,
                lastModifiedDate: doc.lastModifiedDate ? new Date(doc.lastModifiedDate) : new Date(),
                lastModifiedByUserId: doc.lastModifiedByUserId || 0,
                active: doc.active || false
              };

              if (!isDocumentInfo(documentInfo)) {
                observer.error(new Error('Invalid document data structure'));
                return;
              }

              observer.next(documentInfo);
              observer.complete();
            } else {
              observer.error(new Error('Document not found'));
            }
          } catch (err) {
            observer.error(err);
          }
        },
        error => {
          console.error('Error getting document info:', error);
          observer.error(error);
        }
      );
    });
  }

  public getList(env: string = 'core', isResolved: boolean = false): Observable<Reminder[]> {
    const url = `${environment.dotNetApiUrl}/${Area.core}/reminders/List?environment=${env}&isResolved=${isResolved}`;

    return this.httpClient.get<any>(url).pipe(
      map(response => {
        try {
          let reminderResponses: any[] = [];

          if (Array.isArray(response)) {
            reminderResponses = response;
          }
          else if (response && typeof response === 'object' && 'value' in response && Array.isArray(response.value)) {
            reminderResponses = response.value;
          }

          if (!reminderResponses || reminderResponses.length === 0) {
            return [] as Reminder[];
          }


          const reminders = reminderResponses.map(item => {
            try {
              return toReminder(item);
            } catch (err) {
              console.error('Error converting reminder:', err, item);
              return null;
            }
          });

          const validReminders = reminders.filter(r => r !== null);

          return validReminders;
        } catch (err) {
          console.error('Error processing API response:', err);
          return [] as Reminder[];
        }
      }),
      catchError(error => {
        console.error('Error fetching reminders:', error);
        return of([] as Reminder[]);
      })
    );
  }

  public setReminder(reminder: Reminder, env: string = 'core'): Observable<Reminder> {
    const url = `${environment.dotNetApiUrl}/${Area.core}/reminders?environment=${env}`;

    const reminderRequest = toReminderRequest(reminder);

    if (reminderRequest.ID > 0) {
      if (reminderRequest.LastModifiedByUserID === undefined || reminderRequest.LastModifiedByUserID === null) {
        reminderRequest.LastModifiedByUserID = reminderRequest.CreatedByUserID || 0;
      }
    } else {
      reminderRequest.LastModifiedByUserID = reminderRequest.LastModifiedByUserID || 0;
    }

    return this.httpClient.post<any>(url, reminderRequest).pipe(
      map(response => {

        if (response) {
          const convertedReminder = toReminder(response);
          this.notifyReminderUpdated();
          return convertedReminder;
        }

        console.warn('Received empty response from backend, returning original reminder');
        return reminder;
      }),
      catchError(error => {
        console.error('Error saving reminder:', error);
        throw error;
      })
    ) as Observable<Reminder>;
  }

  public setReminderWithDocument(reminder: Reminder, documentFile: File, env: string = 'core'): Observable<Reminder> {
    const url = `${environment.dotNetApiUrl}/${Area.core}/reminders/WithDocument?environment=${env}`;

    const reminderRequest = toReminderRequest(reminder);

    if (reminderRequest.ID > 0) {
      if (reminderRequest.LastModifiedByUserID === undefined || reminderRequest.LastModifiedByUserID === null) {
        reminderRequest.LastModifiedByUserID = reminderRequest.CreatedByUserID || 0;
      }
    } else {
      reminderRequest.LastModifiedByUserID = reminderRequest.LastModifiedByUserID || 0;
    }

    (reminderRequest as any).Priority = reminderRequest.Priority != null 
      ? Number(reminderRequest.Priority)
      : null; 

    const formData = new FormData();
    formData.append('document', documentFile);
    formData.append('reminderJSON', JSON.stringify(reminderRequest));  

    return this.httpClient.post<any>(url, formData).pipe(
      map(response => {
        if (response) {
          const convertedReminder = toReminder(response);
          this.notifyReminderUpdated();
          return convertedReminder;
        }

        console.warn('Received empty response from backend, returning original reminder');
        return reminder;
      }),
      catchError(error => {
        console.error('Error saving reminder with document:', error);
        throw error;
      })
    ) as Observable<Reminder>;
  }
}



// WEBPACK FOOTER //
// ./src/app/core/components/reminders/reminders.service.ts